/**
 * Shared hook: auto-recalculates CDEK delivery cost when cart changes.
 *
 * Used by both cart page and checkout page to ensure a single
 * source of truth. Debounced 500ms, abortable.
 */

import { useEffect, useRef } from 'react';
import { useCart } from '@/features/cart/api/use-cart';
import { useCartFingerprint } from './use-cart-fingerprint';
import {
  useDeliveryStore,
  selectIsCdek,
  selectCityCode,
  selectCartFingerprint,
} from '../model/use-delivery-store';

interface RecalculationResult {
  newCost: number | null;
  oldCost: number | null;
}

export function useDeliveryRecalculation(
  onRecalculated?: (result: RecalculationResult) => void,
) {
  const { data: cartData } = useCart();
  const items = cartData?.data?.items;
  const { cartFingerprint } = useCartFingerprint();

  const isCdek = useDeliveryStore(selectIsCdek);
  const cityCode = useDeliveryStore(selectCityCode);
  const storedFingerprint = useDeliveryStore(selectCartFingerprint);

  const onRecalculatedRef = useRef(onRecalculated);
  onRecalculatedRef.current = onRecalculated;

  // Store initial cart fingerprint when CDEK point is first selected
  useEffect(() => {
    if (isCdek && cityCode && !storedFingerprint && cartFingerprint) {
      useDeliveryStore.getState().updateCdekCost(
        useDeliveryStore.getState().cdekInfo?.cost ?? 0,
        undefined,
        undefined,
        cartFingerprint,
      );
    }
  }, [isCdek, cityCode, storedFingerprint, cartFingerprint]);

  // Auto-recalculate delivery cost when cart changes after point selection
  useEffect(() => {
    if (!isCdek || !cityCode || !storedFingerprint) return;
    if (cartFingerprint === storedFingerprint) return;
    if (!items || items.length === 0) return;

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const tariffCode = useDeliveryStore.getState().cdekInfo?.tariffCode;
        if (!tariffCode) return;

        const res = await fetch('/api/cdek/calculate-real', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            toCityCode: cityCode,
            items: items.map((item) => ({
              productType: item.productType,
              productId: item.productId,
              quantity: item.quantity,
              weight: item.weight,
            })),
          }),
          signal: controller.signal,
        });

        if (!res.ok) return;
        const json = await res.json();

        const cdekInfo = useDeliveryStore.getState().cdekInfo;
        if (!cdekInfo) return;

        const tariffs = json?.tariffs?.all as Array<{
          tariffCode: number;
          deliveryCost: number;
          periodMin: number;
          periodMax: number;
        }> | undefined;
        const best = tariffs?.find((t) => t.tariffCode === tariffCode) ?? tariffs?.[0];

        if (best) {
          const oldCost = cdekInfo.cost;
          useDeliveryStore.getState().updateCdekCost(
            best.deliveryCost,
            best.periodMin,
            best.periodMax,
            cartFingerprint,
          );
          if (best.deliveryCost !== oldCost) {
            onRecalculatedRef.current?.({ newCost: best.deliveryCost, oldCost });
          }
        }
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          console.error('[DeliveryRecalculation] Failed:', e);
        }
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [cartFingerprint, storedFingerprint, isCdek, cityCode, items]);
}
